/**
 * Shipping Management React Query Hooks
 * 
 * Provides React Query hooks for shipping operations including:
 * - Rate shopping and quote comparison
 * - Carrier performance tracking and ranking
 * - Carrier management and configuration
 * 
 * All hooks use React Query for automatic caching, background refetching,
 * and optimistic updates with proper cache invalidation.
 * 
 * @module hooks/useShipping
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shippingService } from '../services/shippingService';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Fetches shipping rate quotes from multiple carriers
 * 
 * Query is disabled if shipmentData is empty or not provided
 * 
 * @param {Object} shipmentData - Shipment details for rate calculation
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with rate quotes
 * @property {Array} data - Array of rate quotes from different carriers
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 * 
 * @example
 * const { data: quotes } = useRateQuotes({
 *   origin_zip: '75001',
 *   destination_zip: '69001',
 *   weight: 5.5,
 *   length: 30,
 *   width: 20,
 *   height: 15
 * });
 */
export const useRateQuotes = (shipmentData, options = {}) => {
  return useQuery({
    queryKey: ['shipping', 'rate-quotes', shipmentData],
    queryFn: () => shippingService.getRateQuotes(shipmentData),
    enabled: !!shipmentData && Object.keys(shipmentData).length > 0,
    ...options,
  });
};

/**
 * Mutation hook to select a specific shipping rate
 * 
 * Automatically invalidates rate quotes and history on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const selectRate = useSelectRate();
 * selectRate.mutate({ 
 *   quoteId: 'quote-123', 
 *   rateId: 'rate-456' 
 * });
 */
export const useSelectRate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ quoteId, rateId }) => shippingService.selectRate(quoteId, rateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping', 'rate-quotes'] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'rate-history'] });
    },
  });
};

/**
 * Fetches historical rate shopping data
 * 
 * @param {Object} [filters={}] - Query filters for date range, carrier, etc.
 * @returns {Object} React Query result with rate history
 * @property {Array} data - Historical rate shopping records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useRateHistory = (filters = {}) => {
  return useQuery({
    queryKey: ['shipping', 'rate-history', filters],
    queryFn: () => shippingService.getRateHistory(filters),
  });
};

/**
 * Fetches carrier performance metrics
 * 
 * @param {Object} [filters={}] - Query filters for warehouse, date range, carrier
 * @returns {Object} React Query result with carrier performance data
 * @property {Object} data - Performance metrics including on-time delivery, damage rates, costs
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useCarrierPerformance = (filters = {}) => {
  return useQuery({
    queryKey: ['shipping', 'carrier-performance', filters],
    queryFn: () => shippingService.getCarrierPerformance(filters),
  });
};

/**
 * Fetches carrier ranking by performance
 * 
 * Query is disabled if warehouseId is not provided
 * 
 * @param {string} warehouseId - Warehouse identifier
 * @param {string} dateFrom - Start date (ISO 8601)
 * @param {string} dateTo - End date (ISO 8601)
 * @returns {Object} React Query result with carrier rankings
 * @property {Array} data - Ranked list of carriers with performance scores
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useCarrierRanking = (warehouseId, dateFrom, dateTo) => {
  return useQuery({
    queryKey: ['shipping', 'carrier-ranking', warehouseId, dateFrom, dateTo],
    queryFn: () => shippingService.getCarrierRanking(warehouseId, dateFrom, dateTo),
    enabled: !!warehouseId,
  });
};

/**
 * Fetches specific carrier metrics over time
 * 
 * Query is disabled if carrierId is not provided
 * 
 * @param {string} carrierId - Carrier identifier
 * @param {string} metric - Metric type (on_time_delivery, damage_rate, cost, etc.)
 * @param {string} dateFrom - Start date (ISO 8601)
 * @param {string} dateTo - End date (ISO 8601)
 * @returns {Object} React Query result with carrier metrics
 * @property {Object} data - Time-series metric data
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useCarrierMetrics = (carrierId, metric, dateFrom, dateTo) => {
  return useQuery({
    queryKey: ['shipping', 'carrier-metrics', carrierId, metric, dateFrom, dateTo],
    queryFn: () => shippingService.getCarrierMetrics(carrierId, metric, dateFrom, dateTo),
    enabled: !!carrierId,
  });
};

/**
 * Fetches list of carriers
 * 
 * @param {Object} [filters={}] - Query filters for active status, service type, etc.
 * @returns {Object} React Query result with carriers list
 * @property {Array} data - List of carrier records
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useCarriers = (filters = {}) => {
  return useQuery({
    queryKey: ['shipping', 'carriers', filters],
    queryFn: () => shippingService.getCarriers(filters),
  });
};

/**
 * Fetches detailed information for a specific carrier
 * 
 * Query is disabled if carrierId is not provided
 * 
 * @param {string} carrierId - Carrier identifier
 * @returns {Object} React Query result with carrier details
 * @property {Object} data - Carrier details including contact info, services, rates
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useCarrierDetails = (carrierId) => {
  return useQuery({
    queryKey: ['shipping', 'carriers', carrierId],
    queryFn: () => shippingService.getCarrierDetails(carrierId),
    enabled: !!carrierId,
  });
};

/**
 * Mutation hook to update carrier configuration
 * 
 * Automatically invalidates carrier details and list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const updateConfig = useUpdateCarrierConfig();
 * updateConfig.mutate({ 
 *   carrierId: 'carrier-123', 
 *   config: { api_key: 'new-key', service_levels: {...} } 
 * });
 */
export const useUpdateCarrierConfig = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ carrierId, config }) => shippingService.updateCarrierConfig(carrierId, config),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shipping', 'carriers', variables.carrierId] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'carriers'] });
    },
  });
};

/**
 * Mutation hook to update a carrier
 * 
 * @returns {Object} React Query mutation result
 */
export const useUpdateCarrier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }) => shippingService.updateCarrierConfig(id, data),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shipping', 'carriers', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'carriers'] });
    },
  });
};

/**
 * Mutation hook to toggle carrier active status
 * 
 * Automatically invalidates carrier details and list on success
 * 
 * @returns {Object} React Query mutation result
 * @property {Function} mutate - Function to trigger mutation
 * @property {boolean} isPending - Mutation pending state
 * 
 * @example
 * const toggleStatus = useToggleCarrierStatus();
 * toggleStatus.mutate({ carrierId: 'carrier-123', isActive: false });
 */
export const useToggleCarrierStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ carrierId, isActive }) => shippingService.toggleCarrierStatus(carrierId, isActive),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['shipping', 'carriers', variables.carrierId] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'carriers'] });
    },
  });
};

/**
 * Mutation hook to create a new carrier
 * 
 * @returns {Object} React Query mutation result
 */
export const useCreateCarrier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data) => shippingService.createCarrier(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping', 'carriers'] });
    },
  });
};

/**
 * Mutation hook to delete a carrier
 * 
 * @returns {Object} React Query mutation result
 */
export const useDeleteCarrier = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (carrierId) => shippingService.deleteCarrier(carrierId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping', 'carriers'] });
    },
  });
};

/**
 * Fetches shipments with optional filtering
 * @param {Object} filters - Query filters for shipments
 * @returns {Object} Query result with shipments
 */
export const useShipments = (filters = {}) => {
  return useQuery({
    queryKey: ['shipping', 'shipments', filters],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.SHIPPING, { params: filters });
      const data = response.data || {};
      return Array.isArray(data.shipments) ? data.shipments : (Array.isArray(data) ? data : []);
    },
  });
};

/**
 * Query hook to fetch shipping statistics
 * 
 * Provides comprehensive shipping statistics including:
 * - Total shipments and delivery status
 * - Today's receiving operations
 * - Stock movements
 * - Delivery performance metrics
 * - Shipping costs
 * 
 * @returns {Object} React Query result with shipping statistics
 * @property {Object} data - Shipping statistics object
 * @property {number} data.totalShipments - Total number of shipments
 * @property {number} data.inTransit - Number of shipments in transit
 * @property {number} data.delivered - Number of delivered shipments
 * @property {number} data.exceptions - Number of shipment exceptions
 * @property {number} data.today_receivings - Number of receiving operations today
 * @property {number} data.receivings_today - Alternative key for today's receivings
 * @property {number} data.movements_today - Number of stock movements today
 * @property {number} data.stock_movements - Alternative key for stock movements
 * @property {string} data.averageDeliveryTime - Average delivery time
 * @property {string} data.onTimeDeliveryRate - On-time delivery rate percentage
 * @property {number} data.totalShippingCost - Total shipping cost
 * @property {number} data.averageShippingCost - Average shipping cost per shipment
 * @property {boolean} isLoading - Loading state
 * @property {boolean} isError - Error state
 */
export const useShippingStats = () => {
  return useQuery({
    queryKey: ['shipping', 'stats'],
    queryFn: async () => {
      const response = await api.get(API_ENDPOINTS.SHIPPING_STATS);
      const data = response.data || {};
      return {
        totalShipments: data.total_shipments || 0,
        inTransit: data.in_transit || 0,
        delivered: data.delivered || 0,
        exceptions: data.exceptions || 0,
        today_receivings: data.today_receivings || data.receivings_today || 0,
        receivings_today: data.receivings_today || data.today_receivings || 0,
        movements_today: data.movements_today || data.stock_movements || 0,
        stock_movements: data.stock_movements || data.movements_today || 0,
        averageDeliveryTime: data.average_delivery_time || '0 days',
        onTimeDeliveryRate: data.on_time_delivery_rate ?? 0,
        totalShippingCost: data.total_shipping_cost || 0,
        averageShippingCost: data.average_shipping_cost || 0
      };
    },
  });
};

/**
 * Fetches shipping trends
 * @returns {Object} Query result with shipping trends
 */
export const useShippingTrends = () => {
  return useQuery({
    queryKey: ['shipping', 'trends'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.SHIPPING}/trends`);
      const data = response.data || {};
      return Array.isArray(data.trends) ? data.trends : [];
    },
  });
};

/**
 * Fetches shipping status distribution
 * @returns {Object} Query result with status distribution
 */
export const useShippingStatusDistribution = () => {
  return useQuery({
    queryKey: ['shipping', 'status-distribution'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.SHIPPING}/status-distribution`);
      const data = response.data || {};
      return Array.isArray(data.distribution) ? data.distribution : [];
    },
  });
};

/**
 * Fetch shipping labels stats
 * @returns {Object} Query result with labels stats
 */
export const useLabelsStats = () => {
  return useQuery({
    queryKey: ['shipping', 'labels', 'stats'],
    queryFn: async () => {
      const response = await api.get(`/api/v1/shipping/labels/stats`);
      const data = response.data || {};
      return {
        totalLabels: data.total_labels || 0,
        generatedToday: data.generated_today || 0,
        templates: data.templates || 0,
        pendingPrint: data.pending_print || 0,
        shippingLabels: data.shipping_labels || 0,
        totalPrinted: data.total_printed || 0
      };
    },
  });
};

/**
 * Fetch shipping labels with filtering and pagination
 * @param {Object} filters - Query filters
 * @returns {Object} Query result with labels
 */
export const useLabels = (filters = {}) => {
  return useQuery({
    queryKey: ['shipping', 'labels', filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.page_size) params.append('page_size', filters.page_size.toString());

      const queryString = params.toString();
      const url = queryString ? `/api/v1/shipping/labels?${queryString}` : `/api/v1/shipping/labels`;
      const response = await api.get(url);
      const data = response.data || {};
      return {
        labels: Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : (Array.isArray(data.labels) ? data.labels : [])),
        total: data.total || data.total_count || 0
      };
    },
  });
};

/**
 * Fetch label templates
 * @returns {Object} Query result with templates
 */
export const useLabelTemplates = () => {
  return useQuery({
    queryKey: ['shipping', 'labels', 'templates'],
    queryFn: async () => {
      const response = await api.get(`/api/v1/shipping/labels/templates`);
      const data = response.data || {};
      return Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
    },
  });
};

/**
 * Fetch label carriers
 * @returns {Object} Query result with carriers
 */
export const useLabelCarriers = () => {
  return useQuery({
    queryKey: ['shipping', 'labels', 'carriers'],
    queryFn: async () => {
      const response = await api.get(`/api/v1/shipping/labels/carriers`);
      const data = response.data || {};
      return Array.isArray(data) ? data : (Array.isArray(data.items) ? data.items : []);
    },
  });
};

/**
 * Generate a shipping label
 * @returns {Object} Mutation object for generating labels
 */
export const useGenerateLabel = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ type, template, order }) => {
      const response = await api.post(`/api/v1/shipping/labels/generate`, {
        type,
        template,
        order
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shipping', 'labels'] });
      queryClient.invalidateQueries({ queryKey: ['shipping', 'labels', 'stats'] });
    },
  });
};

/**
 * Print a shipping label
 * @returns {Object} Mutation object for printing labels
 */
export const usePrintLabel = () => {
  return useMutation({
    mutationFn: async (labelId) => {
      const response = await api.post(`/api/v1/shipping/labels/${labelId}/print`);
      return response.data;
    },
  });
};
